import { SelectionModel } from '@angular/cdk/collections';
import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  effect,
  input,
  model,
  signal,
  untracked,
} from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { SqlAggregateFunctionType } from '@models/helpers';

import {
  ActionEventType,
  ActionType,
  CipoTableOptions,
  ColumnPriorityType,
  DisplayFormats,
  FooterRow,
  TableColumn,
  TablePaginator,
} from '@shared/components/data-list/common';
import { SCREEN_SIZE } from '@shared/consts';

@Component({
  selector: 'cipo-data-list',
  template: '',
})
export abstract class DataListBase<T> {
  options = model<CipoTableOptions<ActionType, T>>({});
  tableColumns = input<TableColumn[]>([]);
  loading = model(true);
  tableData = input<T[]>([]);
  updatedRows = input<T[]>();
  footerRows = input<FooterRow[]>();
  primaryKey = input<string>('entity_instance_id');

  readonly DISPLAY_FORMAT = DisplayFormats;
  readonly FOOTER_KEYS = SqlAggregateFunctionType;

  footerColumns = computed(() => {
    const columns = this.tableColumns();
    const rows = this.footerRows();
    const selectable = untracked(() => this.options().selectable);
    const hasActions = untracked(() => !!this.options().actions);
    const footerColumns = rows.map(row => columns.map(col => `${row.key}_${col.id}`));
    footerColumns.forEach((col, index) => {
      col.unshift(`${rows[index].key}`);
    });
    if (hasActions) {
      footerColumns.forEach(col => {
        col.push('footer_actions');
      });
    }
    return footerColumns;
  });

  @Output() selectionChanged = new EventEmitter<T[]>();
  @Output() actionSelected = new EventEmitter<ActionEventType<number, T>>();
  @Output() paginationUpdated = new EventEmitter<TablePaginator>();
  @Output() sortChanged = new EventEmitter<Sort>();
  @Output() rowClicked = new EventEmitter<T>();

  selection = input(new SelectionModel<T>(true, []));
  deviceWidth = window.innerWidth;
  displayedColumns = signal<string[]>([]);
  totalWidth: number = 0;

  displayFormat = DisplayFormats;
  hasLeftIcons = signal(false);
  hasRightIcons = signal(false);

  @ViewChild(MatSort) sort: MatSort;

  constructor() {
    effect(() => {
      this.tableColumns();
      this.options();
      untracked(() => {
        this.totalWidth = 0;
        this.initializeTableData();
      });
    });

    effect(() => {
      const footerRows = this.footerRows() || [];
      const displayedColumns = this.displayedColumns() || [];
      if (footerRows?.length && displayedColumns[0] !== 'select') {
        displayedColumns.unshift('select');

        untracked(() => this.displayedColumns.set(displayedColumns));
      }
    });
  }

  abstract isAllSelected(): boolean;

  abstract toggleAllRows(): void;

  initializeTableData() {
    this.totalWidth = 0;
    const tableColumns = this.tableColumns();
    let displayedColumns: string[];

    // if at least one column has priority, filter columns by priority
    const hasPriority = tableColumns.findIndex(col => col.hasOwnProperty('priority'));

    if (hasPriority >= 0) {
      const acceptedPrios = this.getAcceptedPriorities();
      displayedColumns = tableColumns
        .map(c => {
          if (!c.filterOnly && acceptedPrios.includes(c.priority)) {
            this.totalWidth += c.width ?? 0;
            return c.name;
          }
          return null;
        })
        .filter(c => !!c);
    } else {
      displayedColumns = tableColumns
        .filter(col => !col.filterOnly)
        .map(col => {
          this.totalWidth += col.width ?? 0;
          return col.name;
        });
    }

    if (this.options()?.selectable) {
      this.totalWidth += 0.5;
      displayedColumns.unshift('select');
    }

    if (this.options()?.actions) {
      this.totalWidth += 0.5;
      displayedColumns.push('actions');
    }

    this.displayedColumns.set(displayedColumns);
  }

  getAcceptedPriorities() {
    const acceptedPriorities: ColumnPriorityType[] = [];
    if (this.deviceWidth > SCREEN_SIZE.phone && this.deviceWidth < SCREEN_SIZE.tablet) {
      acceptedPriorities.push(2, 3);
    }

    if (this.deviceWidth < SCREEN_SIZE.phone) {
      acceptedPriorities.push(1, 3);
    }

    if (this.deviceWidth > SCREEN_SIZE.tablet) {
      acceptedPriorities.push(0, 1, 2, 3);
    }

    return acceptedPriorities;
  }

  selectAction(actionId: number, data: T) {
    this.actionSelected.emit({ actionId, data });
  }

  checked() {
    this.selectionChanged.emit(this.selection().selected);
  }

  rowClick(row: T, event?: MouseEvent) {
    // event.detail is the browser's native click-count for this event; a dblclick's second click reports 2, so skip it.
    if (event?.detail > 1) {
      return;
    }
    this.rowClicked.emit(row);
  }

  onRowKey(event: Event, row: T) {
    if ((row as { empty?: boolean })?.empty) return;
    // Ignore Enter/Space that bubbled up from a nested interactive control
    // (checkbox, button, link) — those have their own keyboard behavior.
    if (event.target !== event.currentTarget) return;
    this.rowClick(row);
  }

  protected checkLeftRightIcons(rows: T[]) {
    const actions = this.options().actions;
    if (!actions) {
      return;
    }
    for (const row of rows || []) {
      // Early exit: no need to inspect further rows once both flags are set
      if (this.hasLeftIcons() && this.hasRightIcons()) break;

      for (const { leftIcon, rightIcon } of actions(row) || []) {
        if (leftIcon) this.hasLeftIcons.set(true);
        if (rightIcon) this.hasRightIcons.set(true);
        if (this.hasLeftIcons() && this.hasRightIcons()) break;
      }
    }
  }
}
